import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Mic, AlertCircle, Loader2, Plus, X, Pencil, Copy, Check, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  TtsVoice,
  TtsVoiceUsage,
  TtsVoicePatch,
  ttsVoicesService,
  clearVoiceListCache,
  apiErrorMessage,
} from '../../services/ttsVoicesService';
import { VoicePreviewButton, useVoicePreview } from '../../components/VoiceSelector';

const USAGES: { id: TtsVoiceUsage; label: string }[] = [
  { id: 'avatar', label: 'Avatar' },
  { id: 'narration', label: 'Narration' },
];

const inputClass = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none';
const labelClass = 'block text-xs font-semibold text-slate-500 mb-1';
const thClass = 'text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap';

interface DetailFields {
  name: string;
  accent: string;
  gender: string;
  character: string;
  wpm: string;
  notes: string;
}

const emptyDetails: DetailFields = { name: '', accent: '', gender: '', character: '', wpm: '', notes: '' };

function detailsFromVoice(v: TtsVoice): DetailFields {
  return {
    name: v.name || '',
    accent: v.accent || '',
    gender: v.gender || '',
    character: v.character || '',
    wpm: v.wpm != null ? String(v.wpm) : '',
    notes: v.notes || '',
  };
}

const NONE = '__none__';

function parseWpm(raw: string): number | undefined | 'invalid' {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 'invalid';
}

const DetailInputs: React.FC<{ value: DetailFields; onChange: (v: DetailFields) => void; placeholderHint?: string }> = ({ value, onChange, placeholderHint }) => {
  const set = (key: keyof DetailFields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange({ ...value, [key]: e.target.value });
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Name</label>
          <input className={inputClass} value={value.name} onChange={set('name')} placeholder={placeholderHint} />
        </div>
        <div>
          <label className={labelClass}>Accent</label>
          <input className={inputClass} value={value.accent} onChange={set('accent')} placeholder={placeholderHint || 'e.g. Irish'} />
        </div>
        <div>
          <label className={labelClass}>Gender</label>
          <input className={inputClass} value={value.gender} onChange={set('gender')} placeholder={placeholderHint || 'e.g. female'} />
        </div>
        <div>
          <label className={labelClass}>Character</label>
          <input className={inputClass} value={value.character} onChange={set('character')} placeholder="e.g. warm, measured" />
        </div>
        <div>
          <label className={labelClass}>Words per minute</label>
          <input className={inputClass} type="number" min={1} value={value.wpm} onChange={set('wpm')} placeholder="e.g. 160" />
        </div>
      </div>
      <div>
        <label className={labelClass}>Notes</label>
        <textarea className={`${inputClass} resize-none`} rows={2} value={value.notes} onChange={set('notes')} />
      </div>
    </>
  );
};

const UsageChips: React.FC<{ value: TtsVoiceUsage[]; onToggle: (u: TtsVoiceUsage) => void; disabled?: boolean }> = ({ value, onToggle, disabled }) => (
  <div className="flex gap-1.5">
    {USAGES.map(u => {
      const on = value.includes(u.id);
      const isLast = on && value.length === 1;
      return (
        <button
          key={u.id}
          type="button"
          onClick={() => onToggle(u.id)}
          disabled={disabled || isLast}
          title={isLast ? 'A voice needs at least one usage' : undefined}
          className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border transition-colors disabled:cursor-not-allowed ${
            on ? 'bg-primary/10 text-primary border-primary/30' : 'bg-white text-slate-400 border-slate-200 hover:bg-slate-50'
          }`}
        >
          {u.label}
        </button>
      );
    })}
  </div>
);

const Toggle: React.FC<{ on: boolean; onClick: () => void; disabled?: boolean; label: string }> = ({ on, onClick, disabled, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className={`relative inline-flex h-5 w-9 rounded-full transition-colors disabled:opacity-50 ${on ? 'bg-primary' : 'bg-slate-200'}`}
  >
    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform mt-0.5 ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
  </button>
);

const AddVoiceForm: React.FC<{ onCreated: (v: TtsVoice) => void; onCancel: () => void }> = ({ onCreated, onCancel }) => {
  const [voiceId, setVoiceId] = useState('');
  const [details, setDetails] = useState<DetailFields>(emptyDetails);
  const [usage, setUsage] = useState<TtsVoiceUsage[]>(['avatar']);
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleUsage = (u: TtsVoiceUsage) =>
    setUsage(prev => (prev.includes(u) ? prev.filter(x => x !== u) : [...prev, u]));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!voiceId.trim()) { setError('Voice ID is required'); return; }
    if (usage.length === 0) { setError('Pick at least one usage'); return; }
    const wpm = parseWpm(details.wpm);
    if (wpm === 'invalid') { setError('Words per minute must be a positive number'); return; }
    setSaving(true);
    try {
      const voice = await ttsVoicesService.create({
        voiceId: voiceId.trim(),
        name: details.name.trim() || undefined,
        accent: details.accent.trim() || undefined,
        gender: details.gender.trim() || undefined,
        character: details.character.trim() || undefined,
        notes: details.notes.trim() || undefined,
        wpm,
        usage,
        enabled,
      });
      onCreated(voice);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to add voice'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-navy">Add voice</h2>
          <p className="text-xs text-slate-500 mt-0.5">Blank name, accent and gender are filled in from ElevenLabs.</p>
        </div>
        <button type="button" onClick={onCancel} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"><X size={16} /></button>
      </div>
      {error && (
        <div className="px-5 py-4 border-b border-[#e2e8f0] bg-red-50 flex gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-red-700">{error}</p>
        </div>
      )}
      <div className="p-5 flex flex-col gap-4">
        <div>
          <label className={labelClass}>ElevenLabs voice ID *</label>
          <input className={`${inputClass} font-mono`} value={voiceId} onChange={e => setVoiceId(e.target.value)} placeholder="e.g. 21m00Tcm4TlvDq8ikWAM" autoFocus />
        </div>
        <DetailInputs value={details} onChange={setDetails} placeholderHint="Auto-filled if blank" />
        <div className="flex flex-wrap items-center gap-6">
          <div>
            <label className={labelClass}>Usage</label>
            <div className="flex gap-4">
              {USAGES.map(u => (
                <label key={u.id} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-primary rounded" checked={usage.includes(u.id)} onChange={() => toggleUsage(u.id)} />
                  {u.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className={labelClass}>Enabled</label>
            <Toggle on={enabled} onClick={() => setEnabled(v => !v)} label={enabled ? 'Enabled' : 'Disabled'} />
          </div>
        </div>
      </div>
      <div className="px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
          Cancel
        </button>
        <button type="submit" disabled={saving} className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add voice
        </button>
      </div>
    </form>
  );
};

const EditVoiceModal: React.FC<{ voice: TtsVoice; onClose: () => void; onSaved: (v: TtsVoice) => void }> = ({ voice, onClose, onSaved }) => {
  const [details, setDetails] = useState<DetailFields>(() => detailsFromVoice(voice));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setError('');
    const wpm = parseWpm(details.wpm);
    if (wpm === 'invalid') { setError('Words per minute must be a positive number'); return; }
    if (!details.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    try {
      const original = detailsFromVoice(voice);
      const patch: TtsVoicePatch = {};
      (['name', 'accent', 'gender', 'character', 'notes'] as const).forEach(key => {
        if (details[key].trim() !== original[key]) patch[key] = details[key].trim();
      });
      if (wpm !== voice.wpm) patch.wpm = wpm ?? null;
      if (Object.keys(patch).length === 0) { onClose(); return; }
      onSaved(await ttsVoicesService.update(voice.voiceId, patch));
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to save voice'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-navy">Edit voice</h3>
            <p className="text-xs text-slate-500 mt-0.5 font-mono">{voice.voiceId}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"><X size={16} /></button>
        </div>
        <div className="flex flex-col gap-4">
          <DetailInputs value={details} onChange={setDetails} />
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
              <AlertCircle size={16} className="shrink-0" /> {error}
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-5">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
          >
            {saving && <Loader2 size={14} className="animate-spin" />} Save
          </button>
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default function TtsVoicesAdmin() {
  const [voices, setVoices] = useState<TtsVoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [usageFilter, setUsageFilter] = useState<'' | TtsVoiceUsage>('');
  const [showDisabled, setShowDisabled] = useState(true);
  const [accentFilter, setAccentFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<TtsVoice | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const preview = useVoicePreview();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setVoices(await ttsVoicesService.list(undefined, true));
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to load voices'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const replaceVoice = (v: TtsVoice) => {
    clearVoiceListCache();
    setVoices(prev => {
      const idx = prev.findIndex(x => x.voiceId === v.voiceId);
      if (idx === -1) return [v, ...prev];
      const next = [...prev];
      next[idx] = v;
      return next;
    });
  };

  const patchVoice = async (voice: TtsVoice, patch: TtsVoicePatch) => {
    setBusyId(voice.voiceId);
    try {
      replaceVoice(await ttsVoicesService.update(voice.voiceId, patch));
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to update voice'));
    } finally {
      setBusyId(null);
    }
  };

  const toggleUsage = (voice: TtsVoice, u: TtsVoiceUsage) => {
    const next = voice.usage.includes(u) ? voice.usage.filter(x => x !== u) : [...voice.usage, u];
    if (next.length === 0) return;
    patchVoice(voice, { usage: next });
  };

  const copyId = async (voiceId: string) => {
    try {
      await navigator.clipboard.writeText(voiceId);
      setCopiedId(voiceId);
      setTimeout(() => setCopiedId(c => (c === voiceId ? null : c)), 1500);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return voices
      .filter(v => showDisabled || v.enabled)
      .filter(v => !usageFilter || v.usage.includes(usageFilter))
      .filter(v => !accentFilter || (accentFilter === NONE ? !v.accent : v.accent === accentFilter))
      .filter(v => !genderFilter || (genderFilter === NONE ? !v.gender : v.gender?.toLowerCase() === genderFilter))
      .filter(v => !sourceFilter || v.source === sourceFilter)
      .filter(v => !q || [v.label, v.voiceId, v.accent, v.gender, v.character, v.notes, v.source]
        .some(f => (f || '').toLowerCase().includes(q)))
      .sort((a, b) => (a.accent || '').localeCompare(b.accent || '') || a.label.localeCompare(b.label));
  }, [voices, search, usageFilter, accentFilter, genderFilter, sourceFilter, showDisabled]);

  const options = useMemo(() => {
    const distinct = (vals: (string | undefined)[]) => Array.from(new Set(vals.filter((v): v is string => !!v))).sort();
    return {
      accents: distinct(voices.map(v => v.accent)),
      genders: distinct(voices.map(v => v.gender?.toLowerCase())),
      sources: distinct(voices.map(v => v.source)),
      missingAccent: voices.some(v => !v.accent),
      missingGender: voices.some(v => !v.gender),
    };
  }, [voices]);

  const filtersActive = !!(search || usageFilter || accentFilter || genderFilter || sourceFilter);
  const clearFilters = () => {
    setSearch(''); setUsageFilter(''); setAccentFilter(''); setGenderFilter(''); setSourceFilter('');
  };

  const enabledCount = voices.filter(v => v.enabled).length;

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full flex flex-col gap-6">

        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Mic size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Voices</h1>
              <p className="text-slate-500 text-sm font-medium">
                Managed ElevenLabs voices for avatars and narration. {enabledCount} enabled of {voices.length}.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} title="Refresh" className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
              <RefreshCw size={16} />
            </button>
            {!showAdd && (
              <button onClick={() => setShowAdd(true)} className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm">
                <Plus size={16} /> Add voice
              </button>
            )}
          </div>
        </header>

        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
            <AlertCircle size={16} className="shrink-0" /> {error}
          </div>
        )}

        {showAdd && (
          <AddVoiceForm
            onCancel={() => setShowAdd(false)}
            onCreated={v => {
              replaceVoice(v);
              setShowAdd(false);
              toast.success(`Added ${v.label}`);
            }}
          />
        )}

        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#e2e8f0]">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Filter by name, accent, ID, notes…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-[#e2e8f0] rounded-xl text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
              </div>
              <select
                value={usageFilter}
                onChange={e => setUsageFilter(e.target.value as '' | TtsVoiceUsage)}
                className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
              >
                <option value="">All usages</option>
                {USAGES.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
              </select>
              <select value={accentFilter} onChange={e => setAccentFilter(e.target.value)} className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white">
                <option value="">All accents</option>
                {options.accents.map(a => <option key={a} value={a}>{a}</option>)}
                {options.missingAccent && <option value={NONE}>No accent set</option>}
              </select>
              <select value={genderFilter} onChange={e => setGenderFilter(e.target.value)} className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white">
                <option value="">All genders</option>
                {options.genders.map(g => <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>)}
                {options.missingGender && <option value={NONE}>No gender set</option>}
              </select>
              <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white">
                <option value="">All sources</option>
                {options.sources.map(src => <option key={src} value={src}>{src}</option>)}
              </select>
              {filtersActive && (
                <button onClick={clearFilters} className="text-xs font-semibold text-primary hover:underline">Clear filters</button>
              )}
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 accent-primary rounded" checked={showDisabled} onChange={e => setShowDisabled(e.target.checked)} />
                Show disabled
              </label>
              <span className="ml-auto text-xs text-slate-400">{filtered.length} voice{filtered.length !== 1 ? 's' : ''}</span>
            </div>
          </div>

          {loading && (
            <div className="flex justify-center items-center py-20 text-slate-400">
              <Loader2 size={24} className="animate-spin" />
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <Mic size={32} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No voices match</p>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                    <th className={thClass} />
                    <th className={thClass}>Voice</th>
                    <th className={thClass}>Voice ID</th>
                    <th className={thClass}>Accent</th>
                    <th className={thClass}>Gender</th>
                    <th className={thClass}>WPM</th>
                    <th className={thClass}>Usage</th>
                    <th className={thClass}>Enabled</th>
                    <th className={thClass}>Source</th>
                    <th className={thClass}>Notes</th>
                    <th className={thClass} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {filtered.map(v => {
                    const busy = busyId === v.voiceId;
                    return (
                      <tr key={v.voiceId} className={`hover:bg-slate-50/60 transition-colors ${v.enabled ? '' : 'opacity-60'}`}>
                        <td className="px-4 py-3">
                          <VoicePreviewButton voiceId={v.voiceId} playingId={preview.playingId} loadingId={preview.loadingId} onToggle={preview.toggle} size={30} />
                        </td>
                        <td className="px-4 py-3 text-navy font-medium">{v.label}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => copyId(v.voiceId)}
                            title="Copy voice ID"
                            className="flex items-center gap-1.5 font-mono text-xs text-slate-500 hover:text-primary transition-colors"
                          >
                            {v.voiceId}
                            {copiedId === v.voiceId ? <Check size={12} className="text-primary" /> : <Copy size={12} />}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{v.accent || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap capitalize">{v.gender || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{v.wpm ?? '—'}</td>
                        <td className="px-4 py-3">
                          <UsageChips value={v.usage} onToggle={u => toggleUsage(v, u)} disabled={busy} />
                        </td>
                        <td className="px-4 py-3">
                          <Toggle on={v.enabled} disabled={busy} onClick={() => patchVoice(v, { enabled: !v.enabled })} label={v.enabled ? 'Disable voice' : 'Enable voice'} />
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{v.source}</td>
                        <td className="px-4 py-3 text-slate-600 max-w-[260px]">
                          <p className="truncate text-xs" title={v.notes}>{v.notes || '—'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {busy && <Loader2 size={14} className="animate-spin text-slate-400" />}
                            <button onClick={() => setEditing(v)} title="Edit details" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                              <Pencil size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <EditVoiceModal
          voice={editing}
          onClose={() => setEditing(null)}
          onSaved={v => {
            replaceVoice(v);
            setEditing(null);
            toast.success('Voice updated');
          }}
        />
      )}
    </div>
  );
}
